import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatINR, formatDate } from "../lib/api";
import { PageHeader, StatusBadge, LoadingRows, KPICard } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Battery, Thermometer, Activity, TrendingUp, ShieldCheck, Wrench, Gauge, IndianRupee } from "lucide-react";
import { IoTDevicePanel } from "../components/IoTDevicePanel";

const STAGE_LABEL = { procured: "Procured", warehouse: "Warehouse", assigned: "Assigned", installed: "Installed", activated: "Activated", revenue_generating: "Revenue", maintenance: "Maintenance", recovered: "Recovered", refurbished: "Refurbished", redeployed: "Redeployed", retired: "Retired" };

export default function AssetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [twin, setTwin] = useState(null);
  useEffect(() => { api.get(`/assets/${id}/twin`).then((r) => setTwin(r.data)); }, [id]);
  if (!twin) return <LoadingRows rows={6} />;
  const { asset: a, health, valuation, iot, timeline } = twin;
  return (
    <>
      <PageHeader
        title={`Asset · ${a.serial_number}`}
        description={<span className="flex items-center gap-2"><StatusBadge status={a.status} /> {a.model} · {a.capacity_kw} kW</span>}
        actions={<Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>}
      />

      {/* Digital twin */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Health Score</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-heading text-5xl font-semibold">{health.score}</div>
            <div className="text-sm text-muted-foreground">/ 100</div>
          </div>
          <Progress value={health.score} className="mt-3 h-2" />
          <div className="mt-3 text-sm"><span className="text-muted-foreground">Risk:</span> <span className="capitalize font-medium">{health.risk}</span></div>
          <div className="text-sm"><span className="text-muted-foreground">Remaining life:</span> {health.remaining_useful_life_years} yrs</div>
          <div className="text-sm mt-2 text-blue-600 dark:text-blue-400">{health.recommended_action}</div>
        </Card>

        <Card className="p-5 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Valuation</div>
          <div className="mt-2"><span className="font-heading text-3xl font-semibold">{formatINR(valuation.current_value)}</span></div>
          <div className="text-xs text-muted-foreground">Current asset value</div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <div><div className="text-muted-foreground text-xs">Landed cost</div>{formatINR(valuation.landed_cost)}</div>
            <div><div className="text-muted-foreground text-xs">Lifetime profit</div>{formatINR(valuation.lifetime_profit)}</div>
            <div><div className="text-muted-foreground text-xs">ROI</div>{valuation.roi_percent}%</div>
            <div><div className="text-muted-foreground text-xs">Revenue</div>{formatINR(a.revenue_generated || 0)}</div>
          </div>
        </Card>

        <Card className="p-5 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">IoT Snapshot</div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2"><Battery className="w-4 h-4 text-emerald-500" /> {iot.asset_health}% health</div>
            <div className="flex items-center gap-2"><Gauge className="w-4 h-4 text-blue-500" /> {iot.utilization_percent}% util</div>
            <div className="flex items-center gap-2"><Thermometer className="w-4 h-4 text-amber-500" /> {iot.temperature_c}°C</div>
            <div className="flex items-center gap-2"><Activity className="w-4 h-4 text-fuchsia-500" /> {iot.charging_cycles} cycles</div>
            <div className="col-span-2 text-xs text-muted-foreground">Backup: {iot.daily_backup_hours} hrs/day · Runtime: {iot.monthly_runtime_hours} hrs/mo</div>
            <div className="col-span-2 text-xs">Charging behaviour: <span className="font-medium">{iot.charging_behaviour}</span></div>
          </div>
        </Card>
      </div>

      {/* Live IoT (only if linked to a Flexi Twin device) */}
      {a.manufacturer_device_id && <IoTDevicePanel deviceId={a.manufacturer_device_id} />}

      {/* Timeline */}
      <Card className="p-5 rounded-xl">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Lifecycle timeline</div>
        <div className="relative">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-border" />
          <div className="space-y-4">
            {timeline.map((t) => (
              <div key={t.stage} className="flex items-start gap-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 ${t.at ? "bg-blue-600 border-blue-600" : "bg-background border-muted-foreground/40"}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{STAGE_LABEL[t.stage]}</div>
                  <div className="text-xs text-muted-foreground">{t.at ? formatDate(t.at) : "Pending"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Identity + Financial + Warranty */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Identity</div>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-muted-foreground">Serial</dt><dd className="font-mono">{a.serial_number}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Model</dt><dd>{a.model}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Capacity</dt><dd>{a.capacity_kw} kW</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Voltage</dt><dd>{a.voltage} V</dd></div>
          </dl>
        </Card>
        <Card className="p-5 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Warranty</div>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-muted-foreground">Start</dt><dd>{formatDate(a.warranty_start)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">End</dt><dd>{formatDate(a.warranty_end)}</dd></div>
          </dl>
        </Card>
        <Card className="p-5 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Deployment</div>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-muted-foreground">Installed</dt><dd>{formatDate(a.installation_date)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Outlet</dt><dd className="truncate max-w-[140px]">{a.outlet_id?.slice(0, 10) || "-"}…</dd></div>
          </dl>
        </Card>
      </div>
    </>
  );
}
